import React, { useCallback, useEffect, useMemo } from 'react'
import { Dropdown, makeStyles, mergeClasses, Option } from '@fluentui/react-components'
import { useSearchParams } from 'react-router-dom'
import { t } from '../../i18n/i18n'
import { ActivityStatus } from '../../api/schemas/schema'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { ACTIVITY_STATUS_ALL } from '../../helpers/calendarHelper'

export const STATUS_PARAM = 'status'

const useStyles = makeStyles({
   dropdown: {
      minWidth: '150px',
      '@media screen and (max-width: 1007px)': {
         width: '50%',
      },
      '@media screen and (max-width: 640px)': {
         width: '100%',
      },
   },
})

type ActivityStatusFilterProps = {
   className?: string
}

/**
 * Filters a list of activities by status. Ongoing is the default, so a completed activity drops out of
 * the view until it is filtered for. The selection lives in the URL (?status=1) so it survives a reload
 * and can be linked to, following the same pattern as ActivityPeriodFilter and CostFilters. The stored
 * value is the enum ordinal, so it is language independent.
 */
export const ActivityStatusFilter = ({ className }: ActivityStatusFilterProps) => {
   const classes = useStyles()
   const [searchParams, setSearchParams] = useSearchParams()
   const statusParam = searchParams.get(STATUS_PARAM)

   const setStatus = useCallback(
      (value: string) => {
         // Ongoing is the default view, so it needs no parameter in the URL.
         if (value === `${ActivityStatus.Ongoing}`) {
            searchParams.delete(STATUS_PARAM)
         } else {
            searchParams.set(STATUS_PARAM, value)
         }
         setSearchParams(searchParams)
      },
      [searchParams, setSearchParams]
   )

   // Drop a value a hand-edited or stale URL may carry, so the URL always reflects what is shown.
   useEffect(() => {
      if (statusParam !== null && statusParam !== ACTIVITY_STATUS_ALL && statusParam !== `${ActivityStatus.Completed}`) {
         setStatus(`${ActivityStatus.Ongoing}`)
      }
   }, [statusParam, setStatus])

   // ActivityStatus members are named after existing translation keys (Ongoing, Completed), so the
   // labels come straight out of the locale files.
   const statusOptions = useMemo(
      () => [...enumValuesToOptions(ActivityStatus).map((x) => ({ value: `${x.key}`, text: x.text })), { value: ACTIVITY_STATUS_ALL, text: t('All') }],
      []
   )

   const selectedValue = statusParam === ACTIVITY_STATUS_ALL || statusParam === `${ActivityStatus.Completed}` ? statusParam : `${ActivityStatus.Ongoing}`
   const selectedStatusText = statusOptions.find((x) => x.value === selectedValue)?.text ?? null

   return (
      <Dropdown
         aria-label={t('FilterByStatus')}
         className={mergeClasses(classes.dropdown, className ?? '')}
         value={selectedStatusText}
         selectedOptions={[selectedValue]}
         onOptionSelect={(e, data) => setStatus(data.optionValue ?? `${ActivityStatus.Ongoing}`)}
      >
         {statusOptions.map((x) => (
            <Option key={x.value} value={x.value}>
               {x.text}
            </Option>
         ))}
      </Dropdown>
   )
}
